import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ContextService } from '../../services/context.service';
import { NotificationPreferencesService } from '../../services/notification-preferences.service';
import { PortalSelectorComponent } from '../portal-selector/portal-selector.component';
import { AUTH_CONFIG } from '../../auth.config';
import { FeatureFlagService } from '../../feature-flags/feature-flag.service';
import { evaluatePendingNotificationSetupIfNeeded } from '../../utils/notification-preferences.helpers';
import { firstValueFrom } from 'rxjs';
import { filter, timeout } from 'rxjs/operators';
import { isAuth0SessionDead } from '../../utils/auth0-errors';
import {
  allowsEmptyLoginScopeForLegalResourcesAdmin,
  needsAttorneyFirmSelection,
  needsClientPortalSelection
} from '../../utils/portal-login-scope';

@Component({
  selector: 'lr-callback',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PortalSelectorComponent],
  templateUrl: './callback.component.html',
  styleUrls: ['./callback.component.scss']
})
export class CallbackComponent implements OnInit {
  private authService = inject(AuthService);
  contextService = inject(ContextService);
  private notificationPreferencesService = inject(NotificationPreferencesService);
  private router = inject(Router);
  private config = inject(AUTH_CONFIG);
  private featureFlagService = inject(FeatureFlagService);

  isLoading = signal(true);
  errorMessage = signal<string | null>(null);
  showPortalSelector = signal(false);

  currentPortalKey = this.config.portalKey;

  async ngOnInit() {
    await this.handleCallback();
  }

  private async handleCallback(): Promise<void> {
    try {
      await firstValueFrom(this.authService.handleRedirectCallback());

      await firstValueFrom(
        this.authService.isLoading$.pipe(
          filter(isLoading => !isLoading),
          timeout(10000)
        )
      );

      try {
        await firstValueFrom(this.authService.getAccessTokenSilently());
      } catch (err) {
        if (isAuth0SessionDead(err) && this.currentPortalKey.toLowerCase() === 'client') {
          this.contextService.rejectSession();
          await this.router.navigate(['/unauthorized'], { replaceUrl: true });
          return;
        }
        throw err;
      }

      try {
        await firstValueFrom(
          // First login may run legacy provision. Auth guard stays at 20s.
          this.contextService.loadUserContext(this.currentPortalKey).pipe(timeout(60000))
        );
      } catch (err) {
        if (err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403)) {
          if (err.status === 403) {
            await this.router.navigate(['/access-denied'], { replaceUrl: true });
          } else {
            this.contextService.rejectSession();
            await this.router.navigate(['/unauthorized'], { replaceUrl: true });
          }
          this.isLoading.set(false);
          return;
        }
        throw err;
      }

      await firstValueFrom(this.featureFlagService.load(this.currentPortalKey));
      await this.selectPortal(this.currentPortalKey);
    } catch (err) {
      console.error('Callback: Error during callback flow', err);
      this.isLoading.set(false);
      this.errorMessage.set('Error processing authentication. Please try again.');
    }
  }

  async onPortalSelected(portalKey: string): Promise<void> {
    this.showPortalSelector.set(false);
    this.isLoading.set(true);
    await this.selectPortal(portalKey);
  }

  private async selectPortal(portalKey: string): Promise<void> {
    try {
      this.contextService.setActivePortal(portalKey);

      const permissions = this.contextService.getPermissions();
      const hasPermissionRows = (permissions?.permissions?.length ?? 0) > 0;
      const hasClients = (permissions?.clients?.length ?? 0) > 0;
      const hasFirms = (permissions?.firms?.length ?? 0) > 0;
      const roles = this.contextService.getRoles();
      const allowEmptyScopeForLrAdmin = allowsEmptyLoginScopeForLegalResourcesAdmin(
        portalKey,
        roles,
        hasPermissionRows,
        hasClients,
        hasFirms
      );
      if (!hasPermissionRows && !hasClients && !hasFirms && !allowEmptyScopeForLrAdmin) {
        await this.router.navigate(['/access-denied'], { replaceUrl: true });
        return;
      }

      await firstValueFrom(
        this.contextService.contextReady$.pipe(
          filter(ready => ready === true),
          timeout(5000)
        )
      );

      const portalKeyName = this.currentPortalKey.toLowerCase();
      if (portalKeyName === 'client') {
        await this.navigateClientPortal();
      } else if (portalKeyName === 'attorney') {
        await this.navigateAttorneyPortal();
      } else {
        await this.router.navigateByUrl(`/${portalKey}`.toLowerCase(), { replaceUrl: true });
      }
    } catch (err) {
      console.error('Callback: Error selecting portal', err);
      this.isLoading.set(false);
      this.errorMessage.set('Error loading portal permissions. Please try again.');
    }
  }

  private async navigateAttorneyPortal(): Promise<void> {
    const groups = this.contextService.getFirmGroups();
    const roles = this.contextService.getRoles();

    if (groups.length === 0) {
      await this.router.navigate(['/access-denied'], { replaceUrl: true });
      return;
    }

    if (needsAttorneyFirmSelection(roles, groups.length)) {
      await this.router.navigate(['/firm-selection'], { replaceUrl: true });
      return;
    }

    this.contextService.setSelectedFirm(groups[0].firmId);
    await this.router.navigate(['/home'], { replaceUrl: true });
  }

  private async navigateClientPortal(): Promise<void> {
    const clients = this.contextService.getClients();
    const roles = this.contextService.getRoles();

    if (needsClientPortalSelection(roles, clients.length)) {
      await this.router.navigate(['/client-selection'], { replaceUrl: true });
      return;
    }

    if (clients.length > 0) {
      this.contextService.setSelectedClient(clients[0].mCode);
    }

    await evaluatePendingNotificationSetupIfNeeded(
      this.contextService,
      this.notificationPreferencesService,
      this.contextService.getSelectedClient()?.mCode
    );

    if (this.contextService.isPendingNotificationSetup()) {
      await this.router.navigate(['/notification-setup'], { replaceUrl: true });
    } else {
      await this.router.navigate(['/dashboard'], { replaceUrl: true });
    }
  }

  retryLogin(): void {
    this.errorMessage.set(null);
    this.isLoading.set(true);
    this.authService.login();
  }
}

